import { Box, ButtonGroup, Button, Typography, Tooltip, Menu, MenuItem, ListItemIcon } from "@mui/material";
import { IconButton } from "@zesty-io/material";
import { SaveRounded, CloseRounded, CloudUploadRounded, ArrowDropDownRounded, CalendarTodayRounded, DeleteRounded } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams as useRouterParams } from "react-router";
import { useStagedChanges } from "./StagedChangesContext";
import { useCreateItemsPublishingMutation, useDeleteContentItemsMutation, useUpdateContentItemsMutation } from "../../../../../../shell/services/instance";
import { useMetaKey } from "../../../../../../shell/hooks/useMetaKey";
import { ConfirmPublishesModal } from "./ConfirmPublishesDialog";
import { useSelectedItems } from "./SelectedItemsContext";
import { SchedulePublishesModal } from "./SchedulePublishesDialog";
import { ConfirmDeletesDialog } from "./ConfirmDeletesDialog";
import { notify } from "../../../../../../shell/store/notifications";
import { useDispatch } from "react-redux";
import { usePermission } from "../../../../../../shell/hooks/use-permissions";
import { ContentItem } from "../../../../../../shell/services/types";
import { fetchAllModelPublishings, fetchItem } from "../../../../../../shell/store/content";
type UpdateListActionsProps = {
  items: ContentItem[];
};
export const UpdateListActions = ({
  items
}: UpdateListActionsProps) => {
  const {
    t
  } = useTranslation();
  const {
    modelZUID
  } = useRouterParams<{
    modelZUID: string;
  }>();
  const canPublish = usePermission("PUBLISH", modelZUID);
  const canDelete = usePermission("DELETE", modelZUID);
  const canUpdate = usePermission("UPDATE", modelZUID);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);
  const [itemsToPublish, setItemsToPublish] = useState<string[]>([]);
  const [itemsToSchedule, setItemsToSchedule] = useState<string[]>([]);
  const [showPublishesModal, setShowPublishesModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDeletesModal, setShowDeletesModal] = useState(false);
  const {
    stagedChanges,
    updateStagedChanges,
    clearStagedChanges
  } = useStagedChanges();
  const [selectedItems, setSelectedItems] = useSelectedItems();
  const [updateContentItems] = useUpdateContentItemsMutation();
  const [createItemsPublishing] = useCreateItemsPublishingMutation();
  const [deleteContentItems, {
    isLoading: isDeleting
  }] = useDeleteContentItemsMutation();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Selected/staged ZUIDs can go stale (e.g. after a bulk delete) before the
  // selection is cleared. Resolve to ContentItems and drop anything that no
  // longer exists instead of letting callers crash on `item.meta`.
  const resolveItems = (ids: string[]): ContentItem[] => (ids ?? []).map(id => items?.find(item => item.meta.ZUID === id)).filter((item): item is ContentItem => Boolean(item));
  const saveShortcut = useMetaKey("s", () => {
    handleSave();
  });
  const publishShortcut = useMetaKey("p", () => {
    if (!canPublish) return;
    if (hasStagedChanges) {
      handleSaveAndPublish();
    } else {
      setItemsToPublish(selectedItems);
    }
  });
  const hasStagedChanges = Object.keys(stagedChanges).length > 0;
  const handleSave = async () => {
    try {
      setIsSaving(true);
      await updateContentItems({
        modelZUID,
        body: Object.entries(stagedChanges).map(([id, changes]: any) => {
          const item = items.find((item: ContentItem) => item.meta.ZUID === id);
          return {
            ...item,
            data: {
              ...item.data,
              ...changes
            }
          };
        })
      });
      await Promise.allSettled([Object.keys(stagedChanges).forEach(itemZUID => {
        dispatch(fetchItem(modelZUID, itemZUID));
      })]);
      setIsSaving(false);
      clearStagedChanges({});
    } catch (err) {
      setIsSaving(false);
      dispatch(notify({
        kind: "error",
        message: t("content.itemListErrorSaving", {
          error: err?.data?.error
        })
      }));
    }
  };
  const handleSaveAndPublish = async (isSchedule = false) => {
    try {
      setIsSaving(true);
      const response = await updateContentItems({
        modelZUID,
        body: Object.entries(stagedChanges).map(([id, changes]: any) => {
          const item = items.find(item => item.meta.ZUID === id);
          return {
            ...item,
            data: {
              ...item.data,
              ...changes
            }
          };
        })
      });
      await Promise.allSettled([Object.keys(stagedChanges).forEach(itemZUID => {
        dispatch(fetchItem(modelZUID, itemZUID));
      })]);
      setIsSaving(false);
      if (isSchedule) {
        // @ts-ignore
        setItemsToSchedule([...response?.data?.data]);
      } else {
        // @ts-ignore
        setItemsToPublish([...response?.data?.data]);
      }
    } catch (err) {
      setIsSaving(false);
      dispatch(notify({
        kind: "error",
        message: t("content.itemListErrorSaving", {
          error: err?.data?.error
        })
      }));
    }
  };
  useEffect(() => {
    if (itemsToPublish.length) {
      setShowPublishesModal(true);
    }
  }, [itemsToPublish]);
  useEffect(() => {
    if (itemsToSchedule.length) {
      setShowScheduleModal(true);
    }
  }, [itemsToSchedule]);
  return <>
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" data-sentry-element="Box" data-sentry-source-file="UpdateListActions.tsx">
        <Box display={"flex"} gap={1.5} alignItems="center" data-sentry-element="Box" data-sentry-source-file="UpdateListActions.tsx">
          <IconButton size="small" onClick={() => {
          clearStagedChanges({});
          setSelectedItems([]);
        }} data-sentry-element="IconButton" data-sentry-source-file="UpdateListActions.tsx">
            <CloseRounded sx={{
            fontSize: "20px"
          }} data-sentry-element="CloseRounded" data-sentry-source-file="UpdateListActions.tsx" />
          </IconButton>
          <Typography variant="h3" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="UpdateListActions.tsx">
            {hasStagedChanges ? t("content.itemListUpdateCount", {
            count: Object.keys(stagedChanges)?.length
          }) : t("content.itemListSelectedCount", {
            count: selectedItems?.length
          })}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center" data-sentry-element="Box" data-sentry-source-file="UpdateListActions.tsx">
          {hasStagedChanges && canUpdate ? <Tooltip enterDelay={1000} enterNextDelay={1000} title={<div>
                  {t("content.itemListSaveItemsTooltip")} <br />
                  {saveShortcut}
                </div>}>
              <Button data-cy="MultiPageTableSaveChanges" variant="contained" startIcon={<SaveRounded />} size="small" onClick={handleSave} loading={isSaving}>
                {t("common.save")}
              </Button>
            </Tooltip> : canDelete ? <Button data-cy="MultiPageTableDelete" loading={isDeleting} onClick={() => {
          setShowDeletesModal(true);
        }} startIcon={<DeleteRounded color="action" />} size="small" variant="outlined" color="inherit">
              {t("common.delete")}
            </Button> : null}
          {canPublish && canUpdate && <ButtonGroup variant="contained" color="success" size="small" sx={{
          "& .MuiButtonGroup-grouped:not(:last-of-type)": {
            borderColor: "green.600"
          }
        }}>
              <Tooltip enterDelay={1000} enterNextDelay={1000} title={<div>
                    {hasStagedChanges ? t("content.itemListSavePublishItemsTooltip") : t("content.itemListPublishItemsTooltip")}{" "}
                    <br />
                    {publishShortcut}
                  </div>}>
                <Button startIcon={<CloudUploadRounded />} sx={{
              color: "common.white",
              whiteSpace: "nowrap"
            }} onClick={() => {
              if (hasStagedChanges) {
                handleSaveAndPublish();
              } else {
                setItemsToPublish(selectedItems);
              }
            }} loading={isPublishing || isSaving} color="success" variant="contained" data-cy="MultiPageTablePublish">
                  {hasStagedChanges ? t("content.itemListSavePublish") : t("content.itemListPublish")}
                </Button>
              </Tooltip>
              <Button sx={{
            color: "common.white",
            width: 32,
            // Override MUI default minWidth of 40px one-off
            minWidth: "unset !important"
          }} onClick={event => {
            setAnchorEl(event.currentTarget);
          }} disabled={isPublishing || isSaving}>
                <ArrowDropDownRounded fontSize="small" />
              </Button>
            </ButtonGroup>}
          <Menu onClose={() => setAnchorEl(null)} anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }} transformOrigin={{
          vertical: -8,
          horizontal: "right"
        }} anchorEl={anchorEl} open={!!anchorEl} data-sentry-element="Menu" data-sentry-source-file="UpdateListActions.tsx">
            <MenuItem onClick={() => {
            if (hasStagedChanges) {
              handleSaveAndPublish();
            } else {
              setItemsToPublish(selectedItems);
            }
            setAnchorEl(null);
          }} data-sentry-element="MenuItem" data-sentry-source-file="UpdateListActions.tsx">
              <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="UpdateListActions.tsx">
                <CloudUploadRounded fontSize="small" data-sentry-element="CloudUploadRounded" data-sentry-source-file="UpdateListActions.tsx" />
              </ListItemIcon>
              {hasStagedChanges ? t("content.itemListSavePublishNow") : t("content.itemListPublishNow")}
            </MenuItem>
            <MenuItem onClick={() => {
            if (hasStagedChanges) {
              handleSaveAndPublish(true);
            } else {
              setItemsToSchedule(selectedItems);
            }
            setAnchorEl(null);
          }} data-sentry-element="MenuItem" data-sentry-source-file="UpdateListActions.tsx">
              <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="UpdateListActions.tsx">
                <CalendarTodayRounded fontSize="small" data-sentry-element="CalendarTodayRounded" data-sentry-source-file="UpdateListActions.tsx" />
              </ListItemIcon>
              {hasStagedChanges ? t("content.itemListSaveSchedulePublish") : t("content.itemListSchedulePublish")}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      {showPublishesModal && <ConfirmPublishesModal items={resolveItems(itemsToPublish)} onCancel={() => {
      setItemsToPublish([]);
      clearStagedChanges({});
      setShowPublishesModal(false);
    }} loading={isPublishing} onConfirm={items => {
      if (!items.length) {
        setIsPublishing(false);
        setItemsToPublish([]);
        clearStagedChanges({});
        setShowPublishesModal(false);
        setSelectedItems([]);
        return;
      }
      setIsPublishing(true);
      createItemsPublishing({
        modelZUID,
        body: items?.map(item => {
          return {
            ZUID: item.meta.ZUID,
            version: item.meta.version,
            publishAt: "now",
            unpublishAt: "never"
          };
        })
      }).unwrap().then(async () => {
        await dispatch(fetchAllModelPublishings({
          modelZUID
        }));
        setItemsToPublish([]);
        clearStagedChanges({});
        setSelectedItems([]);
        setShowPublishesModal(false);
      }).catch(res => {
        setIsPublishing(false);
        dispatch(notify({
          kind: "error",
          message: t("content.itemListErrorPublishing", {
            error: res?.data?.error
          })
        }));
      });
    }} />}
      {showScheduleModal && <SchedulePublishesModal items={resolveItems(itemsToSchedule)} onCancel={() => {
      setItemsToSchedule([]);
      clearStagedChanges({});
      setShowScheduleModal(false);
    }} loading={isPublishing} onConfirm={(items, publishDateTime) => {
      if (!items.length) {
        setIsPublishing(false);
        setItemsToSchedule([]);
        clearStagedChanges({});
        setShowScheduleModal(false);
        setSelectedItems([]);
        return;
      }
      setIsPublishing(true);
      createItemsPublishing({
        modelZUID,
        body: items?.map(item => {
          return {
            ZUID: item.meta.ZUID,
            version: item.meta.version,
            publishAt: publishDateTime ? publishDateTime : "now",
            unpublishAt: "never"
          };
        })
      }).unwrap().then(async response => {
        await dispatch(fetchAllModelPublishings({
          modelZUID
        }));
        setItemsToSchedule([]);
        clearStagedChanges({});
        setSelectedItems([]);
        setShowScheduleModal(false);
      }).catch(res => {
        setIsPublishing(false);
        dispatch(notify({
          kind: "error",
          message: t("content.itemListErrorPublishing", {
            error: res?.data?.error
          })
        }));
      });
    }} />}
      {showDeletesModal && <ConfirmDeletesDialog items={resolveItems(selectedItems)} onCancel={() => {
      setShowDeletesModal(false);
    }} onConfirm={async items => {
      if (!items.length) {
        setSelectedItems([]);
        return;
      }
      await deleteContentItems({
        modelZUID,
        body: items.map(item => item.meta.ZUID)
      }).unwrap();
      items.forEach(item => {
        dispatch({
          type: "REMOVE_ITEM",
          itemZUID: item.meta.ZUID
        });
      });
      setSelectedItems([]);
    }} />}
    </>;
};